<template>
  <div class="bind-container">
    <div class="background-decoration">
      <div class="circle circle-1" />
      <div class="circle circle-2" />
      <div class="circle circle-3" />
      <div class="circle circle-4" />
      <div class="grid-pattern" />
    </div>

    <div class="brand-section">
      <img src="/static/anjunLogo.png" alt="Anjun" class="brand-logo">
      <div class="brand-slogan">{{ $t('login.brandSlogan') }}</div>
    </div>

    <div class="bind-box">
      <div class="header">
        <img src="/static/logo/feishu-logo.png" alt="飞书" class="feishu-logo">
        <h2 class="title">{{ $t('login.feishuBindTitle') }}</h2>
        <p class="subtitle">{{ $t('login.feishuBindSubtitle') }}</p>
      </div>

      <div class="form-box">
        <div class="input-group">
          <div class="input-wrapper">
            <i class="el-icon-user input-icon" />
            <input
              v-model.trim="bindForm.username"
              :placeholder="$t('login.inputNumber')"
              type="text"
              class="input-field"
              @keyup.enter.prevent="handleBind"
            >
          </div>
        </div>

        <div class="input-group">
          <div class="input-wrapper">
            <i class="el-icon-lock input-icon" />
            <input
              v-model.trim="bindForm.password"
              :placeholder="$t('login.passwordPrompt')"
              type="password"
              class="input-field"
              @keyup.enter.prevent="handleBind"
            >
          </div>
        </div>

        <div class="button-group">
          <button class="bind-button" :disabled="loading" @click="handleBind">
            <i v-if="loading" class="el-icon-loading" />
            <span>{{ loading ? $t('login.processing') : $t('login.confirmBind') }}</span>
          </button>
          <button class="back-button" @click="goBack">
            {{ $t('login.backToLogin') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import md5 from 'js-md5';
import { feishuNoLoginBind } from '@/api/user';
import { setToken } from '@/utils/auth';

export default {
  name: 'FeishuBind',
  data() {
    return {
      bindForm: {
        username: '',
        password: ''
      },
      bindVerify: '', // 飞书登录返回的验证码
      loading: false
    };
  },
  created() {
    // 检查是否为葡语环境，如果是则切换为葡语
    const currentLocale = this.$i18n.locale;
    const browserLanguage = (navigator.language || navigator.browserLanguage).toLowerCase();
    
    if (browserLanguage.startsWith('pt') || browserLanguage.startsWith('pt-br')) {
      if (currentLocale !== 'pu') {
        this.$store.dispatch('language/setLanguage', 'pu');
      }
    }
    
    // 获取从飞书登录回调传递过来的 bindVerify 参数
    this.bindVerify = this.$route.query.bindVerify;
    if (!this.bindVerify) {
      this.$message({
        message: this.$t('login.invalidBindCode'),
        type: 'error'
      });
      this.$router.push({ path: '/login' });
    }
  },
  methods: {
    handleBind() {
      if (!this.bindForm.username) {
        this.$message({
          message: this.$t('login.inputNumber'),
          type: 'warning'
        });
        return;
      }
      if (!this.bindForm.password) {
        this.$message({
          message: this.$t('login.passwordPrompt'),
          type: 'warning'
        });
        return;
      }

      this.loading = true;
      const password = md5(md5(this.bindForm.password));

      feishuNoLoginBind({
        username: this.bindForm.username,
        password: password,
        bindVerify: this.bindVerify
      })
        .then((response) => {
          this.loading = false;
          if (response.code === 200) {
            const data = response.data || response;
            const token = data.token || data.data?.token;
            
            if (token) {
              setToken(token);
              this.$store.commit('user/SET_TOKEN', token);
            }
            
            this.$message({
              message: this.$t('login.bindSuccess'),
              type: 'success'
            });

            // 清空缓存的视图
            this.$store.state.tagsView.cachedViews = [];
            this.$store.state.tagsView.visitedViews = [];

            // 设置超时时间
            const overTime = new Date().getTime() + 12 * 60 * 60 * 1000;
            sessionStorage.setItem('overTime', overTime);

            // 启动定时器
            this.throttling(12 * 60 * 60 * 1000);

            // 获取用户信息并生成路由
            this.getUserInfoAndRedirect();
          } else {
            this.$message({
              message: response.msg || this.$t('login.bindFailed'),
              type: 'error'
            });
          }
        })
        .catch((error) => {
          this.loading = false;
          console.error('绑定失败:', error);
          this.$message({
            message: error.message || this.$t('login.bindFailed'),
            type: 'error'
          });
        });
    },

    async getUserInfoAndRedirect() {
      try {
        // 获取用户信息
        const { permission } = await this.$store.dispatch('user/getInfo');
        // 根据角色生成可访问的路由
        const accessRoutes = await this.$store.dispatch('permission/generateRoutes', permission.menuPermissions);
        // 动态添加可访问的路线
        this.$router.addRoutes(accessRoutes);
        // 跳转到首页
        this.$router.push({ path: '/' });
      } catch (error) {
        console.error('获取用户信息失败:', error);
        this.$message({
          message: this.$t('login.getUserInfoFailed'),
          type: 'error'
        });
      }
    },

    goBack() {
      this.$router.push({ path: '/login' });
    },

    throttling(wait) {
      const thatt = this;
      let tiems = 0;
      const now = new Date().getTime();
      if (now - tiems > wait) {
        tiems = setTimeout(function() {
          thatt.$router.push({ path: '/login' });
          clearTimeout(tiems);
          return;
        }, wait);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.bind-container {
  --anjun-green: #00A933;
  --anjun-green-dark: #008a29;
  --anjun-green-light: #00cc3f;
  --brand-primary: #00A933;
  --brand-secondary: #42b983;

  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  padding: 20px;
  position: relative;
  overflow: hidden;

  .background-decoration {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 0;

    .circle {
      position: absolute;
      border-radius: 50%;
      opacity: 0.08;
      animation: float 20s ease-in-out infinite;
    }

    .circle-1 {
      width: 600px;
      height: 600px;
      background: linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-secondary) 100%);
      top: -200px;
      right: -200px;
      animation-delay: 0s;
    }

    .circle-2 {
      width: 400px;
      height: 400px;
      background: linear-gradient(135deg, var(--brand-secondary) 0%, var(--brand-primary) 100%);
      bottom: -150px;
      left: -150px;
      animation-delay: -5s;
    }

    .circle-3 {
      width: 300px;
      height: 300px;
      background: linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-secondary) 100%);
      top: 50%;
      left: 10%;
      animation-delay: -10s;
    }

    .circle-4 {
      width: 250px;
      height: 250px;
      background: linear-gradient(135deg, var(--brand-secondary) 0%, var(--brand-primary) 100%);
      top: 20%;
      right: 20%;
      animation-delay: -15s;
    }

    @keyframes float {
      0%, 100% {
        transform: translate(0, 0) scale(1);
      }
      25% {
        transform: translate(30px, -30px) scale(1.05);
      }
      50% {
        transform: translate(-20px, 20px) scale(0.95);
      }
      75% {
        transform: translate(20px, 30px) scale(1.02);
      }
    }

    .grid-pattern {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-image: 
        linear-gradient(rgba(0, 169, 51, 0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(0, 169, 51, 0.03) 1px, transparent 1px);
      background-size: 50px 50px;
      opacity: 0.5;
    }
  }

  .brand-section {
    position: absolute;
    left: 60px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    animation: fadeInLeft 1s ease-out;

    @keyframes fadeInLeft {
      from {
        opacity: 0;
        transform: translateY(-50%) translateX(-50px);
      }
      to {
        opacity: 1;
        transform: translateY(-50%) translateX(0);
      }
    }

    .brand-logo {
      width: auto;
      height: auto;
      margin-bottom: 24px;
      filter: drop-shadow(0 10px 30px rgba(0, 169, 51, 0.3));
      animation: pulse 3s ease-in-out infinite;
    }

    @keyframes pulse {
      0%, 100% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.05);
      }
    }

    .brand-name {
      font-size: 48px;
      font-weight: 800;
      background: linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-secondary) 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      margin-bottom: 12px;
      letter-spacing: 2px;
    }

    .brand-slogan {
      font-size: 16px;
      color: #666;
      font-weight: 500;
      letter-spacing: 3px;
      text-transform: uppercase;
    }
  }

  .bind-box {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(20px);
    border-radius: 24px;
    padding: 50px 60px;
    width: 100%;
    max-width: 480px;
    box-shadow: 
      0 25px 50px rgba(0, 0, 0, 0.1),
      0 10px 20px rgba(0, 0, 0, 0.05),
      inset 0 1px 0 rgba(255, 255, 255, 0.8);
    animation: slideUp 0.8s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 2;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: linear-gradient(90deg, var(--brand-primary) 0%, var(--brand-secondary) 100%);
      border-radius: 24px 24px 0 0;
    }

    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(40px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .header {
      text-align: center;
      margin-bottom: 40px;

      .feishu-logo {
        width: 80px;
        height: 80px;
        margin-bottom: 20px;
        animation: rotateIn 0.8s ease-out;
        filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.1));
      }

      @keyframes rotateIn {
        from {
          opacity: 0;
          transform: rotate(-180deg) scale(0.5);
        }
        to {
          opacity: 1;
          transform: rotate(0) scale(1);
        }
      }

      .title {
        font-size: 28px;
        font-weight: 700;
        color: #333;
        margin: 0 0 10px 0;
        background: linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-secondary) 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }

      .subtitle {
        font-size: 14px;
        color: #999;
        margin: 0;
      }
    }

    .form-box {
      .input-group {
        margin-bottom: 20px;

        .input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          background: #f7f8fa;
          border: 2px solid transparent;
          border-radius: 12px;
          padding: 15px 20px;
          transition: all 0.3s ease;

          &:focus-within {
            background: #fff;
            border-color: var(--brand-primary);
            box-shadow: 0 0 0 4px rgba(0, 169, 51, 0.1);
          }

          .input-icon {
            font-size: 20px;
            color: #999;
            margin-right: 12px;
            transition: color 0.3s ease;
          }

          &:focus-within .input-icon {
            color: var(--brand-primary);
          }

          .input-field {
            flex: 1;
            border: none;
            background: transparent;
            font-size: 15px;
            color: #333;
            outline: none;
            width: 100%;

            &::placeholder {
              color: #c0c4cc;
            }
          }
        }
      }

      .button-group {
        margin-top: 30px;

        .bind-button {
          width: 100%;
          padding: 15px;
          border: none;
          border-radius: 12px;
          background: linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-secondary) 100%);
          color: #fff;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-bottom: 15px;
          box-shadow: 0 4px 15px rgba(0, 169, 51, 0.3);

          &:hover:not(:disabled) {
            transform: translateY(-2px);
            background: linear-gradient(135deg, var(--brand-secondary) 0%, var(--brand-primary) 100%);
            box-shadow: 0 8px 25px rgba(0, 169, 51, 0.4);
          }

          &:active:not(:disabled) {
            transform: translateY(0);
          }

          &:disabled {
            opacity: 0.7;
            cursor: not-allowed;
          }

          .el-icon-loading {
            animation: rotate 1s linear infinite;
          }

          @keyframes rotate {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        }

        .back-button {
          width: 100%;
          padding: 15px;
          border: 2px solid #e4e7ed;
          border-radius: 12px;
          background: #fff;
          color: #606266;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;

          &:hover {
            border-color: var(--brand-primary);
            color: var(--brand-primary);
            background: #f7f8fa;
          }
        }
      }
    }
  }
}

@media (max-width: 1200px) {
  .bind-container {
    .brand-section {
      display: none;
    }
  }
}
</style>
